'use client';

/**
 * useMemoryPreference — whether Meridian may PASSIVELY save memories about this user.
 *
 * Loads/saves through `{apiBaseUrl}/memories` (GET returns `autoExtract`, PATCH sets it),
 * the same endpoint that lists and deletes memories — one writer, so the chat-settings
 * toggle and the Settings → Preferences toggle can never disagree. Multiple mounted
 * instances stay in sync through a window event fired after every successful save.
 *
 * Opting out stops only the silent background writes; stored memories are still used, and
 * "remember this" / "forget that" still work.
 */

import { useCallback, useEffect, useState } from 'react';
import { getAuthToken } from '../lib/api/authenticated-fetch';

interface MemoryPreferenceResponse {
  autoExtract?: boolean;
}

/** Cross-instance sync signal (chat settings modal ⇄ Hub preferences page). */
const MEMORY_PREFERENCE_CHANGED_EVENT = 'meridian:memory-preference-changed';

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export interface UseMemoryPreferenceOptions {
  apiBaseUrl?: string;
  /** Skip fetching entirely (e.g. a closed modal). */
  enabled?: boolean;
}

export function useMemoryPreference(options: UseMemoryPreferenceOptions = {}) {
  const { apiBaseUrl = '/api/meridian', enabled = true } = options;
  // Default-on, matching the server: the toggle never renders as "off" while loading.
  const [autoExtract, setAutoExtract] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/memories`, {
        headers: buildHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load memory settings');
      const data = (await res.json()) as MemoryPreferenceResponse;
      setAutoExtract(data.autoExtract !== false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load memory settings');
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  // Refetch when another instance saves (modal ⇄ settings page sync).
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const onChanged = () => {
      load();
    };
    window.addEventListener(MEMORY_PREFERENCE_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(MEMORY_PREFERENCE_CHANGED_EVENT, onChanged);
  }, [enabled, load]);

  const saveAutoExtract = useCallback(
    async (next: boolean): Promise<boolean> => {
      setIsSaving(true);
      setError(null);
      try {
        const res = await fetch(`${apiBaseUrl}/memories`, {
          method: 'PATCH',
          headers: buildHeaders(),
          credentials: 'include',
          body: JSON.stringify({ autoExtract: next }),
        });
        if (!res.ok) throw new Error('Failed to save memory preference');
        const data = (await res.json()) as MemoryPreferenceResponse;
        setAutoExtract(data.autoExtract !== false);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event(MEMORY_PREFERENCE_CHANGED_EVENT));
        }
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save memory preference');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBaseUrl]
  );

  return {
    autoExtract,
    isLoading,
    isSaving,
    error,
    reload: load,
    saveAutoExtract,
  };
}
